/**
 * Apollo's sticky add-to-cart bar (mobile).
 *
 * Shows a slim fixed bottom bar on product pages at mobile widths whenever
 * the main Add to Cart button is scrolled out of view. Visibility is computed
 * from the button's position on a rAF-throttled scroll/resize listener, which
 * is reliable across all rendering contexts. The bar's button clicks the real
 * submit button, so all of Dawn's product form logic runs unchanged. Hidden
 * while a text field has focus so it never covers the keyboard on
 * personalization inputs.
 */
(() => {
  const bar = document.querySelector('.apollos-sticky-atc');
  if (!bar) return;

  const sectionId = bar.dataset.sectionId;
  const submitButton = document.getElementById(`ProductSubmitButton-${sectionId}`);
  if (!submitButton) return;

  const stickyButton = bar.querySelector('[data-sticky-submit]');
  const stickyPrice = bar.querySelector('[data-sticky-price]');
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const typingSelector = 'input[type="text"], input[type="number"], input[type="email"], input[type="search"], textarea';

  let typing = false;
  let ticking = false;

  const buttonOutOfView = () => {
    const rect = submitButton.getBoundingClientRect();
    // No layout box (e.g. display:none) — treat as "in view" so the bar stays hidden.
    if (rect.width === 0 && rect.height === 0) return false;
    return rect.bottom < 0 || rect.top > window.innerHeight;
  };

  const updateVisibility = () => {
    const show = mobileQuery.matches && !typing && buttonOutOfView();
    bar.toggleAttribute('hidden', !show);
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(() => {
      updateVisibility();
      ticking = false;
    });
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  if (mobileQuery.addEventListener) mobileQuery.addEventListener('change', updateVisibility);

  // Hide while the on-screen keyboard is open (text/personalization fields).
  document.addEventListener('focusin', (event) => {
    if (event.target.matches(typingSelector)) {
      typing = true;
      updateVisibility();
    }
  });
  document.addEventListener('focusout', () => {
    window.setTimeout(() => {
      const active = document.activeElement;
      typing = !!(active && active.matches(typingSelector));
      updateVisibility();
    }, 100);
  });

  stickyButton.addEventListener('click', () => {
    if (submitButton.disabled || submitButton.getAttribute('aria-disabled') === 'true') return;
    submitButton.click();
  });

  // Mirror the real button's state and the current variant price on changes.
  const syncFromForm = () => {
    const disabled = submitButton.disabled || submitButton.hasAttribute('disabled');
    stickyButton.disabled = disabled;
    const label = submitButton.querySelector('span');
    if (label && label.textContent.trim()) stickyButton.textContent = label.textContent.trim();

    const priceContainer = document.getElementById(`price-${sectionId}`);
    if (priceContainer && stickyPrice) {
      const priceItem = priceContainer.classList.contains('price--on-sale')
        ? priceContainer.querySelector('.price-item--sale')
        : priceContainer.querySelector('.price-item--regular');
      const priceEl = priceItem || priceContainer.querySelector('.price-item');
      if (priceEl && priceEl.textContent.trim()) stickyPrice.textContent = priceEl.textContent.trim();
    }
  };
  syncFromForm();

  const formObserver = new MutationObserver(syncFromForm);
  formObserver.observe(submitButton, { attributes: true, childList: true, subtree: true });
  const priceContainer = document.getElementById(`price-${sectionId}`);
  if (priceContainer) {
    formObserver.observe(priceContainer, { childList: true, subtree: true, characterData: true });
  }

  updateVisibility();
})();
